import { FC, useState, useMemo, useEffect, useCallback, useRef } from 'react'
import { KTSVG } from '../../../../_metronic/helpers'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {
  createInvoice,
  getEligibleInvoiceGoodsReceipts,
  getEligibleInvoicePurchaseOrders,
  getApprovalDocumentAvailableActions,
  getInvoiceApprovalSummaries,
  getInvoicesPage,
  markInvoiceAsPaid,
  getInvoiceById,
  getPurchaseOrderById,
  getGoodsReceiptById,
  getRFQById,
  getRequisitionById,
  getCompanyGroupAdmins,
  getVesselsByCompany,
  markInvoiceAsPartialPaid,
  uploadInvoiceAttachment,
  approveInvoice,
  rejectInvoice,
  reworkInvoice,
  cancelInvoice,
} from '../core/_requests'
import ProcurementAttachmentGallery from './ProcurementAttachmentGallery'
import VesselLandingLink from '../../../shared/components/VesselLandingLink'
import {
  CreateInvoiceRequest,
  Invoice,
  InvoiceDetail,
  InvoiceApprovalSummary,
  InvoiceListRow,
  PurchaseOrder,
  GoodsReceipt,
  CompanyGroupAdmin,
  Vessel,
  Requisition,
  InvoiceEligibleGoodsReceipt,
  InvoiceEligiblePurchaseOrder,
  ApprovalAvailableActions,
  ApprovalInstance,
} from '../core/_models'
import { useAuth } from '../../auth'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import { downloadElementAsPdf } from '../core/exportUtils'
import InvoiceProcurementDocument from './InvoiceProcurementDocument'
import { buildInvoiceThreeWayMatchAmounts, resolveInvoiceThreeWayMatchLabel } from '../core/invoiceThreeWayMatch'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import ApprovalDocumentStatusCard from './ApprovalDocumentStatusCard'
import MoneyDisplay from './MoneyDisplay'
import ProcurementActionGrid from './ProcurementActionGrid'
import {
  resolveInvoiceAmountPresentation,
  resolveInvoiceMoneyPresentation,
  resolveInvoiceOriginalCurrency,
} from '../core/invoiceMoney'
import {resolvePurchaseOrderOriginalCurrency} from '../core/purchaseOrderMoney'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'

const toProcurementListFilters = (globalState: {
  company: {id: number; type: string} | null
  vessel: {id?: number | null} | null
}) => ({
  ...(globalState.company?.id ? {companyId: globalState.company.id, companyType: globalState.company.type} : {}),
  ...(globalState.vessel?.id ? {vesselId: Number(globalState.vessel.id)} : {}),
})

const renderThreeWayMatchBadge = (record: Invoice | InvoiceListRow) => {
  const label =
    record.threeWayMatchStatus === 'MATCHED'
      ? 'Matched'
      : record.threeWayMatchStatus === 'PARTIALLY_MATCHED'
        ? 'Partially Matched'
        : record.threeWayMatchStatus === 'NOT_MATCHED'
          ? 'Not Matched'
          : resolveInvoiceThreeWayMatchLabel(record)
  const summary =
    ('threeWayMatchSummary' in record && record.threeWayMatchSummary) ||
    (record.threeWayMatched
      ? 'Invoice amount matches purchase order and goods have been received.'
      : 'Invoice amount or goods receipt does not fully match the purchase order.')
  const badgeClass =
    label === 'Matched'
      ? 'bg-success'
      : label === 'Partially Matched'
        ? 'bg-info text-dark'
        : 'bg-warning'
  const iconClass =
    label === 'Matched'
      ? 'bi-check-circle'
      : label === 'Partially Matched'
        ? 'bi-dash-circle'
        : 'bi-exclamation-circle'

  return (
    <span className={`badge ${badgeClass}`} title={summary} style={{ cursor: 'help', maxWidth: '180px' }}>
      <i className={`bi ${iconClass} me-1`}></i>
      {label}
    </span>
  )
}

interface CreateInvoiceModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: () => void
}

const resolvePoDisplayCurrency = (po?: Partial<PurchaseOrder> | null): string => {
  if (!po) return 'USD'
  return resolvePurchaseOrderOriginalCurrency(po as PurchaseOrder)
}

const isVendorPortalUser = (currentUser: any) =>
  String(currentUser?.role?.roleType || currentUser?.roleType || '').toUpperCase() === 'VENDOR'

type ApprovalSnapshot = {
  latest: ApprovalInstance | null
  availability: ApprovalAvailableActions | null
}

const approvalStatusBadgeClass = (status?: string) => {
  const normalized = String(status || '').toUpperCase()
  if (normalized === 'APPROVED') return 'bg-success'
  if (normalized === 'REJECTED' || normalized === 'CANCELLED') return 'bg-danger'
  if (normalized === 'REWORK') return 'bg-warning text-dark'
  if (normalized === 'PENDING') return 'bg-info'
  return 'bg-secondary'
}

const formatApprovalStepLabel = (sequence?: number | null) =>
  sequence != null && Number.isFinite(Number(sequence)) ? `Step ${sequence}` : 'No active step'

const resolveInvoiceApprovalDisplayStatus = (invoiceStatus?: string | null) => {
  switch (String(invoiceStatus || '').toUpperCase()) {
    case 'DRAFT':
    case 'SUBMITTED':
      return 'PENDING_APPROVAL'
    case 'APPROVED':
      return 'APPROVED'
    case 'REJECTED':
      return 'REJECTED'
    case 'CANCELLED':
      return 'CANCELLED'
    default:
      return String(invoiceStatus || 'UNKNOWN').toUpperCase()
  }
}

export const CreateInvoiceModal: FC<CreateInvoiceModalProps> = ({ visible, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    purchaseOrderId: '',
    goodsReceiptId: '',
    vendorInvoiceNumber: '',
    invoiceDate: '',
    amount: '',
  })
  const [companies, setCompanies] = useState<CompanyGroupAdmin[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [eligiblePurchaseOrders, setEligiblePurchaseOrders] = useState<InvoiceEligiblePurchaseOrder[]>([])
  const [eligibleGoodsReceipts, setEligibleGoodsReceipts] = useState<InvoiceEligibleGoodsReceipt[]>([])
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('')
  const [selectedVesselId, setSelectedVesselId] = useState<string>('')
  const [errors, setErrors] = useState<{ [key: string]: string }>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isAmountDirty, setIsAmountDirty] = useState(false)
  const [isLoadingEligibility, setIsLoadingEligibility] = useState(false)
  const [selectedAttachments, setSelectedAttachments] = useState<File[]>([])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { getScope } = useProcurementPermissions()
  const invoiceCreateModalScope =
    getScope(PROCUREMENT_PERMS.INVOICE_CREATE) ?? getScope(PROCUREMENT_PERMS.INVOICE_VIEW) ?? 'VESSEL'
  const showCrossTenantInvoiceFilters = invoiceCreateModalScope === 'ALL'

  const selectedPO = useMemo(
    () =>
      eligiblePurchaseOrders.find(
        (purchaseOrder) => Number(purchaseOrder.purchaseOrderId) === Number(formData.purchaseOrderId)
      ),
    [eligiblePurchaseOrders, formData.purchaseOrderId]
  )
  const selectedGR = useMemo(
    () =>
      eligibleGoodsReceipts.find(
        (goodsReceipt) => Number(goodsReceipt.id) === Number(formData.goodsReceiptId)
      ),
    [eligibleGoodsReceipts, formData.goodsReceiptId]
  )
  const todayDate = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const requisitionDateForSelectedPO = selectedPO?.requisitionDate ? String(selectedPO.requisitionDate) : ''
  const visibleEligiblePurchaseOrders = useMemo(
    () =>
      eligiblePurchaseOrders.filter(
        (po) =>
          Number(po.remainingInvoiceableAmount || 0) > 0 ||
          (Array.isArray(po.goodsReceipts) && po.goodsReceipts.length > 0)
      ),
    [eligiblePurchaseOrders]
  )

  const amountSummary = useMemo(() => {
    if (!selectedPO) return null
    const poCurrency = resolvePoDisplayCurrency(selectedPO)
    if (!selectedGR) {
      return {
        currency: poCurrency,
        poTotal: Number(selectedPO.totalAmount || 0),
        poRemaining: Number(selectedPO.remainingInvoiceableAmount || 0),
        eligibleAmount: null,
        alreadyInvoiced: null,
        remaining: Number(selectedPO.remainingInvoiceableAmount || 0),
        expected: 0,
        received: 0,
        vendorName: selectedPO.vendorName || '-',
        requisitionNo: selectedPO.requisitionNo || '-',
        grLabel: null,
      }
    }
    return {
      currency: poCurrency,
      poTotal: Number(selectedPO.totalAmount || 0),
      poRemaining: Number(selectedPO.remainingInvoiceableAmount || 0),
      eligibleAmount: Number((selectedGR.grossAmount ?? selectedGR.eligibleAmount) || 0),
      alreadyInvoiced: Number((selectedGR.alreadyInvoicedAmount ?? selectedGR.invoicedAmount) || 0),
      remaining: Number((selectedGR.invoiceableAmount ?? selectedGR.remainingInvoiceableAmount) || 0),
      expected: Number(selectedGR.expectedQtyTotal || 0),
      received: Number(selectedGR.receivedQtyTotal || 0),
      vendorName: selectedPO.vendorName || '-',
      requisitionNo: selectedPO.requisitionNo || '-',
      grLabel:
        selectedGR.grnNumber ||
        `GR-${selectedGR.id} | Received ${selectedGR.receivedDate || '-'} | Invoiceable ${poCurrency} ${Number(
          (selectedGR.invoiceableAmount ?? selectedGR.remainingInvoiceableAmount) || 0
        ).toFixed(2)}`,
    }
  }, [selectedPO, selectedGR])

  const loadEligiblePurchaseOrders = useCallback(
    async (companyUsername?: string, vesselId?: number) => {
      setIsLoadingEligibility(true)
      try {
        const pos = await getEligibleInvoicePurchaseOrders(companyUsername, vesselId)
        setEligiblePurchaseOrders(
          (pos || []).filter(
            (po) =>
              Number(po.remainingInvoiceableAmount || 0) > 0 ||
              (Array.isArray(po.goodsReceipts) && po.goodsReceipts.length > 0)
          )
        )
        const selectedPoId = Number(formData.purchaseOrderId || 0)
        const nextSelectedPo = (pos || []).find((po) => Number(po.purchaseOrderId) === selectedPoId)
        if (!nextSelectedPo) {
          setEligibleGoodsReceipts([])
          if (selectedPoId > 0) {
            setFormData((prev) => ({ ...prev, purchaseOrderId: '', goodsReceiptId: '', amount: '' }))
            setIsAmountDirty(false)
          }
          return
        }
        const initialGoodsReceipts =
          Array.isArray(nextSelectedPo.goodsReceipts) && nextSelectedPo.goodsReceipts.length > 0
            ? nextSelectedPo.goodsReceipts
            : await getEligibleInvoiceGoodsReceipts(selectedPoId)
        setEligibleGoodsReceipts(initialGoodsReceipts)
      } finally {
        setIsLoadingEligibility(false)
      }
    },
    [formData.purchaseOrderId]
  )

  const handleInputChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    if (name === 'amount') {
      setIsAmountDirty(true)
    }
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }))
    }
    if (name === 'purchaseOrderId') {
      if (!value) {
        setEligibleGoodsReceipts([])
        setFormData((prev) => ({ ...prev, goodsReceiptId: '', amount: '' }))
        setIsAmountDirty(false)
        return
      }
      const selectedPurchaseOrder = eligiblePurchaseOrders.find(
        (purchaseOrder) => Number(purchaseOrder.purchaseOrderId) === Number(value)
      )
      setEligibleGoodsReceipts(selectedPurchaseOrder?.goodsReceipts || [])
      setFormData((prev) => ({ ...prev, goodsReceiptId: '', amount: '' }))
      setIsAmountDirty(false)
      try {
        const receipts = await getEligibleInvoiceGoodsReceipts(parseInt(value, 10))
        setEligibleGoodsReceipts(receipts || [])
      } catch (err) {
        console.error('Error fetching eligible GRs by PO', err)
      }
    }
    if (name === 'goodsReceiptId') {
      setIsAmountDirty(false)
    }
  }

  const handleCompanyChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setSelectedCompanyId(value)
    setSelectedVesselId('')
    setFormData((prev) => ({ ...prev, purchaseOrderId: '', goodsReceiptId: '', amount: '' }))
    setEligibleGoodsReceipts([])
    if (value) {
      try {
        const vs = await getVesselsByCompany(value)
        const vesselList = Array.isArray(vs) ? vs : ((vs as any)?.content ?? [])
        setVessels(vesselList)
      } catch (err) {
        console.error('Error loading vessels', err)
        setVessels([])
      }
      await loadEligiblePurchaseOrders(value, undefined)
      return
    }
    setVessels([])
    await loadEligiblePurchaseOrders(undefined, undefined)
  }

  const handleVesselChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value
    setSelectedVesselId(value)
    setFormData((prev) => ({ ...prev, purchaseOrderId: '', goodsReceiptId: '', amount: '' }))
    setEligibleGoodsReceipts([])
    const vesselId = value ? parseInt(value, 10) : undefined
    await loadEligiblePurchaseOrders(selectedCompanyId || undefined, vesselId)
  }

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {}

    if (!formData.purchaseOrderId) newErrors.purchaseOrderId = 'Purchase Order is required'
    if (!formData.invoiceDate) newErrors.invoiceDate = 'Invoice Date is required'
    if (
      formData.invoiceDate &&
      requisitionDateForSelectedPO &&
      formData.invoiceDate < requisitionDateForSelectedPO
    ) {
      newErrors.invoiceDate = 'Invoice date cannot be before requisition date'
    }
    if (formData.invoiceDate && formData.invoiceDate > todayDate) {
      newErrors.invoiceDate = 'Invoice date cannot be in the future'
    }
    if (!formData.amount) newErrors.amount = 'Amount is required'
    const numericAmount = Number(formData.amount || 0)
    if (formData.amount && numericAmount <= 0) newErrors.amount = 'Amount must be greater than 0'
    const remainingInvoiceableAmount = Number(
      selectedGR?.invoiceableAmount ??
      selectedGR?.remainingInvoiceableAmount ??
      selectedPO?.remainingInvoiceableAmount ??
      0
    )
    if ((selectedGR || selectedPO) && numericAmount > remainingInvoiceableAmount) {
      newErrors.amount = `Amount cannot exceed remaining invoiceable amount (${resolvePoDisplayCurrency(selectedPO)} ${remainingInvoiceableAmount.toFixed(2)})`
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    setIsSubmitting(true)
    try {
      const payload: CreateInvoiceRequest = {
        purchaseOrderId: parseInt(formData.purchaseOrderId, 10),
        goodsReceiptId: formData.goodsReceiptId ? parseInt(formData.goodsReceiptId, 10) : null,
        ...(formData.vendorInvoiceNumber.trim()
          ? {vendorInvoiceNumber: formData.vendorInvoiceNumber.trim()}
          : {}),
        invoiceDate: formData.invoiceDate,
        amount: parseFloat(formData.amount),
      }

      const result = await createInvoice(payload)

      // Upload any selected attachments one-by-one
      if (selectedAttachments.length > 0) {
        let uploadErrors = 0
        for (const file of selectedAttachments) {
          try {
            await uploadInvoiceAttachment(result.id, file)
          } catch (uploadErr) {
            console.error('Failed to upload attachment:', file.name, uploadErr)
            uploadErrors++
          }
        }
        if (uploadErrors > 0) {
          toast.warning(`Invoice created but ${uploadErrors} attachment(s) failed to upload.`)
        } else {
          toast.success(`Invoice created successfully with ${selectedAttachments.length} attachment(s)! Invoice Number: ${result.invoiceNumber}`)
        }
      } else {
        toast.success(`Invoice created successfully! Invoice Number: ${result.invoiceNumber}`)
      }

      await loadEligiblePurchaseOrders(selectedCompanyId || undefined, selectedVesselId ? parseInt(selectedVesselId, 10) : undefined)
      handleClose()
      onSubmit()
    } catch (error: any) {
      console.error('Error creating invoice:', error)
      toast.error('Failed to create invoice')
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetForm = () => {
    setFormData({
      purchaseOrderId: '',
      goodsReceiptId: '',
      vendorInvoiceNumber: '',
      invoiceDate: '',
      amount: '',
    })
    setEligibleGoodsReceipts([])
    setEligiblePurchaseOrders([])
    setIsAmountDirty(false)
    setSelectedCompanyId('')
    setSelectedVesselId('')
    setErrors({})
    setSelectedAttachments([])
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  useEffect(() => {
    if (!visible) return
    const load = async () => {
      try {
        await loadEligiblePurchaseOrders()
        if (showCrossTenantInvoiceFilters) {
          const comps = await getCompanyGroupAdmins()
          setCompanies(comps)
        }
      } catch (err) {
        console.error('Error loading invoice modal data', err)
        toast.error('Failed to load invoice eligibility data')
      }
    }
    void load()
  }, [visible, showCrossTenantInvoiceFilters, loadEligiblePurchaseOrders])

  useEffect(() => {
    if (!selectedPO || isAmountDirty) return
    setFormData((prev) => ({
      ...prev,
      amount: Number(
        selectedGR?.invoiceableAmount ??
        selectedGR?.remainingInvoiceableAmount ??
        selectedPO.remainingInvoiceableAmount ??
        0
      ).toFixed(2),
    }))
  }, [selectedPO, selectedGR, isAmountDirty])

  if (!visible) return null

  return (
    <div
      className="modal fade show d-flex align-items-center justify-content-center"
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div className='modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable procurement-view-modal-dialog' role='document'>
        <div className='modal-content bg-white' style={{ color: '#181C32' }}>
          <div className='modal-header'>
            <div>
              <h5 className='modal-title'>Create New Invoice</h5>
              <p className='text-muted mb-0 fs-7'>Enter invoice details for three-way matching</p>
            </div>
            <button type='button' className='btn-close' onClick={handleClose}></button>
          </div>

          <div className='modal-body' style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <div className='row g-3'>
              {showCrossTenantInvoiceFilters && (
                <>
                  <div className='col-md-6'>
                    <label className='form-label fw-semibold fs-6 mb-2'>Company</label>
                    <SearchableSelect className='form-select' value={selectedCompanyId} onChange={handleCompanyChange}>
                      <option value=''>All Companies</option>
                      {companies.map(c => (
                        <option key={c.id} value={c.username || c.id.toString()}>
                          {c.name || c.username || c.id}
                        </option>
                      ))}
                    </SearchableSelect>
                  </div>
                  <div className='col-md-6'>
                    <label className='form-label fw-semibold fs-6 mb-2'>Vessel</label>
                    <SearchableSelect className='form-select' value={selectedVesselId} onChange={handleVesselChange} disabled={!selectedCompanyId}>
                      <option value=''>All Vessels</option>
                      {vessels.map(v => (
                        <option key={v.id} value={v.id}>{v.fleet_name || (v as any).fleetName || `Vessel ${v.id}`}</option>
                      ))}
                    </SearchableSelect>
                  </div>
                </>
              )}

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Purchase Order</label>
                <SearchableSelect
                  className={`form-select ${errors.purchaseOrderId ? 'is-invalid' : ''}`}
                  aria-label='Purchase Order'
                  name='purchaseOrderId'
                  value={formData.purchaseOrderId}
                  onChange={handleInputChange}
                  disabled={isLoadingEligibility}
                >
                  <option value=''>Select Purchase Order</option>
                  {visibleEligiblePurchaseOrders.map(po => (
                    <option key={po.purchaseOrderId} value={po.purchaseOrderId}>
                      {po.poNumber} | Req: {po.requisitionNo || '-'} | {po.status || '-'}
                    </option>
                  ))}
                </SearchableSelect>
                {errors.purchaseOrderId && <div className='invalid-feedback'>{errors.purchaseOrderId}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                  Goods Receipt
                </label>
                <SearchableSelect
                  className={`form-select ${errors.goodsReceiptId ? 'is-invalid' : ''}`}
                  aria-label='Goods Receipt'
                  name='goodsReceiptId'
                  value={formData.goodsReceiptId}
                  onChange={handleInputChange}
                  disabled={!formData.purchaseOrderId}
                >
                  <option value=''>No specific Goods Receipt</option>
                  {eligibleGoodsReceipts.map(gr => (
                  <option key={gr.id} value={gr.id}>
                      {`${gr.grnNumber || `GR-${gr.id}`} | Received ${gr.receivedDate || '-'} | Invoiceable ${resolvePoDisplayCurrency(selectedPO)} ${Number((gr.invoiceableAmount ?? gr.remainingInvoiceableAmount) || 0).toFixed(2)}`}
                    </option>
                  ))}
                </SearchableSelect>
                {errors.goodsReceiptId && <div className='invalid-feedback'>{errors.goodsReceiptId}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Invoice Date</label>
                <input
                  type='date'
                  className={`form-control ${errors.invoiceDate ? 'is-invalid' : ''}`}
                  aria-label='Invoice Date'
                  name='invoiceDate'
                  value={formData.invoiceDate}
                  min={requisitionDateForSelectedPO || undefined}
                  max={todayDate}
                  onChange={handleInputChange}
                  style={{ color: '#000' }}
                />
                {errors.invoiceDate && <div className='invalid-feedback'>{errors.invoiceDate}</div>}
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                  Vendor&apos;s Invoice Number
                </label>
                <input
                  className='form-control'
                  aria-label="Vendor's Invoice Number"
                  name='vendorInvoiceNumber'
                  maxLength={128}
                  value={formData.vendorInvoiceNumber}
                  onChange={handleInputChange}
                  placeholder='Vendor accounting invoice reference'
                  style={{ color: '#000' }}
                />
              </div>

              <div className='col-md-6'>
                <label className='form-label required fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Amount</label>
                <input
                  type='number'
                  step='0.01'
                  className={`form-control ${errors.amount ? 'is-invalid' : ''}`}
                  aria-label='Amount'
                  name='amount'
                  value={formData.amount}
                  onChange={handleInputChange}
                  placeholder='Enter invoice amount'
                  style={{ color: '#000' }}
                />
                {errors.amount && <div className='invalid-feedback'>{errors.amount}</div>}
                {amountSummary && (
                  <div className='form-text text-muted'>
                    <div>PO total: {amountSummary.currency} {amountSummary.poTotal.toFixed(2)}</div>
                    <div>PO remaining invoiceable: {amountSummary.currency} {amountSummary.poRemaining.toFixed(2)}</div>
                    {amountSummary.eligibleAmount != null && (
                      <div>GR gross amount: {amountSummary.currency} {amountSummary.eligibleAmount.toFixed(2)}</div>
                    )}
                    {amountSummary.alreadyInvoiced != null && (
                      <div>Already invoiced on GR: {amountSummary.currency} {amountSummary.alreadyInvoiced.toFixed(2)}</div>
                    )}
                    <div>Remaining invoiceable: {amountSummary.currency} {amountSummary.remaining.toFixed(2)}</div>
                    <div>Vendor: {amountSummary.vendorName}</div>
                    <div>Requisition: {amountSummary.requisitionNo}</div>
                    {amountSummary.expected > 0 && (
                      <div>Received Qty: {amountSummary.received} / {amountSummary.expected}</div>
                    )}
                  </div>
                )}
              </div>

              <div className='col-md-12'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Requisition Date</label>
                <input
                  type='text'
                  className='form-control'
                  value={requisitionDateForSelectedPO || '-'}
                  readOnly
                />
              </div>

              <div className='col-md-12'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>Currency</label>
                <input
                  type='text'
                  className='form-control'
                  value={amountSummary?.currency || resolvePoDisplayCurrency(selectedPO)}
                  readOnly
                />
              </div>

              {/* Attachments */}
              <div className='col-md-12'>
                <label className='form-label fw-semibold fs-6 mb-2' style={{ color: '#181C32' }}>
                  Attachments <span className='text-muted fw-normal'>(optional)</span>
                </label>
                <div
                  className='border rounded p-3 bg-light'
                  style={{ cursor: 'pointer' }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type='file'
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files || [])
                      setSelectedAttachments((prev) => {
                        const existing = new Set(prev.map((f) => f.name + f.size))
                        const newFiles = files.filter((f) => !existing.has(f.name + f.size))
                        return [...prev, ...newFiles]
                      })
                    }}
                  />
                  <div className='d-flex align-items-center gap-2 text-muted'>
                    <i className='bi bi-paperclip fs-5' />
                    <span>Click to attach files</span>
                  </div>
                </div>
                {selectedAttachments.length > 0 && (
                  <div className='mt-2 d-flex flex-column gap-1'>
                    {selectedAttachments.map((file, idx) => (
                      <div key={`${file.name}-${idx}`} className='d-flex align-items-center justify-content-between border rounded px-2 py-1 bg-white'>
                        <span className='text-dark fs-7 text-truncate me-2' title={file.name}>
                          <i className='bi bi-file-earmark me-1' />{file.name}
                        </span>
                        <button
                          type='button'
                          className='btn btn-sm btn-light-danger py-0 px-1'
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedAttachments((prev) => prev.filter((_, i) => i !== idx))
                          }}
                        >
                          <i className='bi bi-x' />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className='modal-footer'>
            <button type='button' className='btn btn-light btn-sm' onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button
              type='button'
              className='btn btn_primary'
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Invoice'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

const numberValue = (value: any): number => {
  const parsed = Number(value ?? 0)
  return Number.isFinite(parsed) ? parsed : 0
}

const displayValue = (value: any): string => {
  if (value === null || value === undefined) return '-'
  const text = String(value).trim()
  return text.length > 0 ? text : '-'
}

const formatMoney = (currency: string, value: any): string => {
  const numeric = numberValue(value)
  return `${displayValue(currency)} ${numeric.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

const InvoiceManagement: FC = () => {
  const { currentUser } = useAuth()
  const { state: globalState } = useGlobalFilter()
  const globalCompanyId = globalState.company?.id ?? null
  const globalCompanyType = globalState.company?.type ?? null
  const globalVesselId = globalState.vessel?.id ? Number(globalState.vessel.id) : null
  const vendorView = isVendorPortalUser(currentUser)
  const {
    can: canProcurementPermission,
    canAny: canAnyProcurementPermission,
    loading: procurementPermissionsLoading,
  } = useProcurementPermissions()
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [activeTab, setActiveTab] = useState<'pending' | 'paid'>('pending')
  const [expandedPaidPoIds, setExpandedPaidPoIds] = useState<Record<number, boolean>>({})
  const [invoiceData, setInvoiceData] = useState<InvoiceListRow[]>([])
  const [serverTotalElements, setServerTotalElements] = useState(0)
  const [serverTotalPages, setServerTotalPages] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [viewInvoice, setViewInvoice] = useState<InvoiceDetail | null>(null)
  const [isViewModalVisible, setIsViewModalVisible] = useState(false)
  const [isViewLoading, setIsViewLoading] = useState(false)
  const [approvalSnapshots, setApprovalSnapshots] = useState<Record<number, ApprovalSnapshot>>({})
  const [invoiceDownloadDoc, setInvoiceDownloadDoc] = useState<{
    invoice: Invoice
    purchaseOrder?: PurchaseOrder | null
    goodsReceipt?: GoodsReceipt | null
    requisition?: Requisition | null
  } | null>(null)
  const [sortConfig, setSortConfig] = useState<{
    key: keyof InvoiceListRow | null
    direction: 'asc' | 'desc'
  }>({ key: null, direction: 'asc' })
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false)

  const canViewInvoice = canProcurementPermission(PROCUREMENT_PERMS.INVOICE_VIEW)
  const canCreateInvoice = canProcurementPermission(PROCUREMENT_PERMS.INVOICE_CREATE)
  const canEditInvoice = canProcurementPermission(PROCUREMENT_PERMS.INVOICE_EDIT)
  const canDownloadInvoice = canAnyProcurementPermission([
    PROCUREMENT_PERMS.INVOICE_DOWNLOAD,
    PROCUREMENT_PERMS.INVOICE_EXPORT,
  ])

  const loadInvoices = useCallback(async () => {
    setIsLoading(true)
    try {
      const pageIndex = Math.max(0, currentPage - 1)
      const invoicesPage = await getInvoicesPage(
        pageIndex,
        rowsPerPage,
        toProcurementListFilters(globalState)
      )
      const content = invoicesPage.content || []

      console.log('Invoices loaded:', content.length)
      setInvoiceData(content)
      setServerTotalElements(invoicesPage.totalElements ?? content.length)
      setServerTotalPages(invoicesPage.totalPages ?? 1)
    } catch (error: any) {
      console.error('Error loading invoices:', error)
      toast.error('Failed to load invoices')
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, rowsPerPage, globalCompanyId, globalCompanyType, globalVesselId])

  useEffect(() => {
    setCurrentPage(1)
  }, [globalCompanyId, globalCompanyType, globalVesselId])

  const loadApprovalSnapshots = useCallback(async (records: InvoiceListRow[]) => {
    const ids = records.map((record) => Number(record.id)).filter((id) => Number.isFinite(id) && id > 0)
    if (ids.length === 0) {
      setApprovalSnapshots({})
      return
    }
    try {
      const summaries = await getInvoiceApprovalSummaries(ids)
      const entries = summaries.map((summary: InvoiceApprovalSummary) => [
        Number(summary.invoiceId),
        {
          latest: {
            status: summary.currentApprovalStatus || 'UNKNOWN',
            currentStepSequence: summary.currentApprovalStage ?? null,
          } as any,
          availability: {
            canSubmit: Boolean(summary.canSubmit),
            canApprove: Boolean(summary.canApprove),
            canReject: Boolean(summary.canReject),
            canRework: Boolean(summary.canRequestRework),
            canCancel: false,
            reason: summary.reason || undefined,
          },
        },
      ] as const)
      setApprovalSnapshots(Object.fromEntries(entries))
    } catch (error) {
      console.error('Error loading invoice approval snapshots:', error)
      setApprovalSnapshots({})
    }
  }, [])

  // Fetch invoices on mount and when explicitly refreshed
  useEffect(() => {
    loadInvoices()
  }, [loadInvoices])

  const handleCreateInvoice = () => {
    setIsCreateModalVisible(true)
  }

  const handleModalClose = () => {
    setIsCreateModalVisible(false)
  }

  const handleInvoiceCreated = () => {
    // Refresh invoice data after creation
    loadInvoices()
  }

  const handleMarkAsPaid = async (invoiceId: number) => {
    if (!canEditInvoice) {
      toast.error('You do not have permission to update invoice payment status')
      return
    }
    if (!window.confirm('Are you sure you want to mark this invoice as paid?')) {
      return
    }

    try {
      await markInvoiceAsPaid(invoiceId)
      toast.success('Invoice marked as paid successfully!')
      loadInvoices() // Refresh the list
    } catch (error: any) {
      console.error('Error marking invoice as paid:', error)
      toast.error('Failed to mark invoice as paid')
    }
  }

  const [isPartialModalVisible, setIsPartialModalVisible] = useState(false)
  const [partialInvoice, setPartialInvoice] = useState<Invoice | InvoiceListRow | null>(null)
  const [partialAmount, setPartialAmount] = useState<string>('')

  const handleMarkPartialPaid = (inv: InvoiceListRow) => {
    if (!canEditInvoice) {
      toast.error('You do not have permission to update invoice payment status')
      return
    }
    void (async () => {
      setPartialInvoice(inv)
      setPartialAmount('')
      setIsPartialModalVisible(true)
    })()
  }

  const submitPartialPaid = async () => {
    if (!partialInvoice) return
    if (!partialAmount.trim()) {
      toast.error('Amount is required for partial payment')
      return
    }
    const num = parseFloat(partialAmount)
    if (isNaN(num) || num <= 0) {
      toast.error('Amount must be greater than 0')
      return
    }
    const total = Number(partialInvoice.amount || 0)
    const paid = Number(partialInvoice.paidAmount || 0)
    const remaining = Math.max(
      Number(partialInvoice.remainingAmount ?? total - paid),
      0
    )
    if (remaining <= 0) {
      toast.error('Invoice is already fully paid')
      return
    }
    if (num > remaining) {
      toast.error(`Amount cannot exceed remaining amount (${partialInvoice.currency} ${remaining.toFixed(2)})`)
      return
    }
    try {
      await markInvoiceAsPartialPaid(partialInvoice.id, num)
      toast.success('Invoice marked as partially paid')
      setIsPartialModalVisible(false)
      setPartialInvoice(null)
      setPartialAmount('')
      loadInvoices()
    } catch (err: any) {
      console.error('Error marking partial paid', err)
      toast.error('Failed to mark invoice as partially paid')
    }
  }

  const handleViewInvoice = async (invoiceId: number) => {
    if (!canViewInvoice) {
      toast.error('You do not have permission to view invoices.')
      return
    }
    setIsViewLoading(true)
    try {
      const inv = await getInvoiceById(invoiceId)
      setViewInvoice(inv)
      setIsViewModalVisible(true)
    } catch (err: any) {
      console.error('Error loading invoice', err)
      toast.error('Failed to load invoice')
    } finally {
      setIsViewLoading(false)
    }
  }

  const handleDownloadInvoicePdf = async (
    invoiceOrId: Invoice | number,
    options?: { silent?: boolean }
  ) => {
    if (!canDownloadInvoice) {
      if (!options?.silent) {
        toast.error('You do not have permission to download invoices')
      }
      return
    }
    const invoiceId = typeof invoiceOrId === 'number' ? invoiceOrId : invoiceOrId.id
    try {
      const invoice =
        typeof invoiceOrId === 'number' ? await getInvoiceById(invoiceId) : invoiceOrId
      const po = invoice.purchaseOrderId
        ? await getPurchaseOrderById(invoice.purchaseOrderId).catch(() => null)
        : null
      const goodsReceipt = invoice.goodsReceiptId
        ? await getGoodsReceiptById(invoice.goodsReceiptId).catch(() => null)
        : null
      const requisitionId = (po as any)?.rfq?.requisition?.id || null
      const requisition = requisitionId
        ? await getRequisitionById(Number(requisitionId)).catch(() => null)
        : null
      setInvoiceDownloadDoc({ invoice, purchaseOrder: po, goodsReceipt, requisition })
      await new Promise((resolve) => window.setTimeout(resolve, 160))
      await downloadElementAsPdf(
        '#invoice-download-host',
        `Invoice_${invoice.invoiceNumber || invoice.id}`
      )
      if (!options?.silent) {
        toast.success('Invoice PDF downloaded')
      }
    } catch (error) {
      console.error('Failed to download invoice PDF', error)
      if (!options?.silent) {
        toast.error('Failed to download invoice PDF')
      }
      throw error
    } finally {
      setInvoiceDownloadDoc(null)
    }
  }

  const handleCloseViewModal = () => {
    setIsViewModalVisible(false)
    setViewInvoice(null)
  }

  // Calculate summary statistics based on API status
  // Summary counts (kept for future dashboard use)
  // const pendingCount = invoiceData.filter(d => d.status === 'PENDING').length
  // const matchedCount = invoiceData.filter(d => d.status === 'THREE_WAY_MATCHED').length
  // const discrepanciesCount = invoiceData.filter(d => d.status === 'DISCREPANCY').length
  // const paidCount = invoiceData.filter(
  //   d => d.status === 'PAID' || (d.status as string) === 'PARTIALLY_PAID'
  // ).length
  // const totalValue = invoiceData.reduce((sum, d) => sum + d.amount, 0)

  const filteredData = useMemo(() => {
    return invoiceData.filter(record => {
      const matchesSearch = searchTerm === '' ||
        record.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(record.vendorInvoiceNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.id.toString().includes(searchTerm.toLowerCase()) ||
        record.purchaseOrderId.toString().includes(searchTerm.toLowerCase()) ||
        String(record.requisitionNo || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (record.requisitionId != null &&
          String(record.requisitionId).includes(searchTerm.toLowerCase()))

      return matchesSearch
    })
  }, [searchTerm, invoiceData])

  // Split into pending (includes partial) vs paid
  const pendingData = useMemo(
    () => filteredData.filter((r) => r.status !== 'PAID'),
    [filteredData]
  )
  const paidData = useMemo(
    () => filteredData.filter((r) => r.status === 'PAID'),
    [filteredData]
  )

  const sortedPending = useMemo(() => {
    let sortedRecords = [...pendingData]

    if (sortConfig.key !== null) {
      sortedRecords.sort((a, b) => {
        const aVal = String(a[sortConfig.key!] || '').toLowerCase()
        const bVal = String(b[sortConfig.key!] || '').toLowerCase()

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
        return 0
      })
    }
    return sortedRecords
  }, [pendingData, sortConfig])

  const sortedPaid = useMemo(() => {
    // Show newest paid first
    return [...paidData].sort((a, b) => b.id - a.id)
  }, [paidData])

  const paidPoGroups = useMemo(() => {
    type PaidPoGroup = {
      poId: number
      poLabel: string
      currency: string
      totalAmount: number
      paidAmount: number
      remainingAmount: number
      latestInvoiceId: number
      matchedCount: number
      invoices: InvoiceListRow[]
    }
    const groups = new Map<number, PaidPoGroup>()
    sortedPaid.forEach((invoice) => {
      const poId = Number(invoice.purchaseOrderId || 0)
      if (poId <= 0) return
      const money = resolveInvoiceMoneyPresentation(invoice, vendorView)
      const paidMoney = resolveInvoiceAmountPresentation(invoice, invoice.paidAmount, vendorView)
      const remainingMoney = resolveInvoiceAmountPresentation(
        invoice,
        invoice.remainingAmount ?? Number(invoice.amount || 0) - Number(invoice.paidAmount || 0),
        vendorView
      )
      const total = Number(money.primaryAmount || 0)
      const paid = Number(paidMoney.primaryAmount || 0)
      const remaining = Number(remainingMoney.primaryAmount || 0)
      const currency = String(money.primaryCurrency || resolveInvoiceOriginalCurrency(invoice) || 'USD').toUpperCase()
      const matched =
        String(invoice.threeWayMatchStatus || '').toUpperCase() === 'MATCHED' || invoice.threeWayMatched === true
      if (!groups.has(poId)) {
        groups.set(poId, {
          poId,
          poLabel: `PO-${poId}`,
          currency,
          totalAmount: 0,
          paidAmount: 0,
          remainingAmount: 0,
          latestInvoiceId: invoice.id,
          matchedCount: 0,
          invoices: [],
        })
      }
      const group = groups.get(poId)!
      group.totalAmount += total
      group.paidAmount += paid
      group.remainingAmount += remaining
      group.latestInvoiceId = Math.max(group.latestInvoiceId, Number(invoice.id || 0))
      group.matchedCount += matched ? 1 : 0
      group.invoices.push(invoice)
    })
    const result = Array.from(groups.values())
    result.forEach((group) => group.invoices.sort((a, b) => b.id - a.id))
    result.sort((a, b) => b.latestInvoiceId - a.latestInvoiceId)
    return result
  }, [sortedPaid])

  const handleSort = (key: keyof InvoiceListRow) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }))
  }

  const pendingTotalElements = sortedPending.length
  const currentRecords = sortedPending
  const totalPages = serverTotalPages || Math.max(1, Math.ceil(pendingTotalElements / rowsPerPage))
  const currentRecordIdsKey = useMemo(
    () => currentRecords.map((record) => `${record.id}:${String(record.status || '').toUpperCase()}`).join(','),
    [currentRecords]
  )

  useEffect(() => {
    void loadApprovalSnapshots(currentRecords)
  }, [currentRecordIdsKey, loadApprovalSnapshots])

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages)
    }
    if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1)
    }
  }, [totalPages, currentPage])

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page)
    }
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(parseInt(e.target.value))
    setCurrentPage(1)
  }

  const getStatusBadge = (status: string) => {
    const statusColors: Record<string, string> = {
      'DRAFT': 'bg-warning',
      'SUBMITTED': 'bg-warning',
      'THREE_WAY_MATCHED': 'bg-warning',
      'PARTIALLY_PAID': 'bg-warning',
      'PAID': 'bg-info'
    }
    const statusLabels: Record<string, string> = {
      'DRAFT': 'Unpaid',
      'SUBMITTED': 'Unpaid',
      'THREE_WAY_MATCHED': 'Unpaid',
      'PARTIALLY_PAID': 'Partial',
      'PAID': 'Paid',
      'REJECTED': 'Rejected'
    }
    return (
      <span className={`badge ${statusColors[status] || 'bg-secondary'} text-white`}>
        {statusLabels[status] || status}
      </span>
    )
  }

  if (!procurementPermissionsLoading && !canViewInvoice) {
    return (
      <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
        <div className='alert alert-warning m-6'>You do not have invoice view access.</div>
      </div>
    )
  }

  return (
    <div className='app-main flex-column flex-row-fluid' id='kt_app_main'>
      <div className='d-flex flex-column flex-column-fluid'>
        <div id='kt_app_content' className='app-content flex-column-fluid bg-white'>
          <div className='card'>
            <div className='card-header border-0 pt-6 bg-white'>
              <div className='d-flex justify-content-between align-items-center w-100'>
                <div>
                  <h3 className='card-label text-dark fw-bold'>Invoice Management</h3>
                  <p className='text-muted mb-0'>Three-way matching of invoices, purchase orders, and goods receipts</p>
                </div>
                {canCreateInvoice && (
                  <button
                    className='btn btn_primary'
                    onClick={handleCreateInvoice}
                  >
                    <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-2' />
                    Create Invoice
                  </button>
                )}
              </div>
            </div>

            <div className='card-body py-4 bg-white border-top'>
              {/* Search Bar */}
              <div className='row gx-3 gy-3 mb-4'>
                <div className='col-md-4'>
                  <label className='form-label text-muted fw-semibold fs-7 mb-2'>Search</label>
                  <div className='position-relative'>
                    <div
                      className='position-absolute ms-3'
                      style={{ top: '50%', transform: 'translateY(-50%)' }}
                    >
                      <KTSVG
                        path='/media/icons/duotune/general/gen021.svg'
                        className='svg-icon-2'
                      />
                    </div>
                    <input
                      type='text'
                      className='form-control form-control-sm ps-10'
                      placeholder='Search by invoice, PO, or requisition...'
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className='d-flex gap-2 mb-4'>
                <button
                  className={`btn btn-sm ${activeTab === 'pending' ? 'btn_primary' : 'btn-light border'}`}
                  onClick={() => {
                    setActiveTab('pending')
                    setCurrentPage(1)
                  }}
                >
                  Pending
                </button>
                <button
                  className={`btn btn-sm ${activeTab === 'paid' ? 'btn_primary' : 'btn-light border'}`}
                  onClick={() => {
                    setActiveTab('paid')
                    setCurrentPage(1)
                  }}
                >
                  Past (Paid)
                </button>
              </div>

              {/* Pending Invoices Table */}
              {activeTab === 'pending' && (
                <div className='mb-8'>
                  <h4 className='fw-bold text-primary mb-4'>Pending Invoices ({pendingTotalElements} entries)</h4>
                  {isLoading ? (
                    <div className='text-center py-5'>
                      <div className='spinner-border text-primary' role='status'>
                        <span className='visually-hidden'>Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className='report-table table-responsive'>
                      <div style={{ overflowX: 'auto' }}>
                        <table className='table table-bordered align-middle'>
                          <thead className='table-header text-start'>
                            <tr>
                              <th onClick={() => handleSort('invoiceNumber')} className='cursor-pointer align-middle' style={{ padding: '12px 16px' }}>
                                <div className='d-flex align-items-center'>
                                  <span style={{ color: '#3F4254', fontWeight: 600 }}>INVOICE NUMBER</span>
                                  <div style={{ transform: 'translateY(-2px)' }}>
                                    <KTSVG
                                      path={`/media/map/sort-col-${sortConfig.key === 'invoiceNumber' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                      className='svg-icon ms-2 custom-sort-icon'
                                    />
                                  </div>
                                </div>
                              </th>
                              <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>VENDOR&apos;S INVOICE NUMBER</th>
                              <th onClick={() => handleSort('invoiceDate')} className='cursor-pointer align-middle' style={{ padding: '12px 16px' }}>
                                <div className='d-flex align-items-center'>
                                  <span style={{ color: '#3F4254', fontWeight: 600 }}>INVOICE DATE</span>
                                  <div style={{ transform: 'translateY(-2px)' }}>
                                    <KTSVG
                                      path={`/media/map/sort-col-${sortConfig.key === 'invoiceDate' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                      className='svg-icon ms-2 custom-sort-icon'
                                    />
                                  </div>
                                </div>
                              </th>
                              <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>PO ID</th>
                              <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>REQ ID</th>
                              <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>REQ NUMBER</th>
                              <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>GR ID</th>
                              <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>VENDOR ID</th>
                              <th onClick={() => handleSort('amount')} className='cursor-pointer align-middle' style={{ padding: '12px 16px' }}>
                                <div className='d-flex align-items-center'>
                                  <span style={{ color: '#3F4254', fontWeight: 600 }}>TOTAL AMOUNT</span>
                                  <div style={{ transform: 'translateY(-2px)' }}>
                                    <KTSVG
                                      path={`/media/map/sort-col-${sortConfig.key === 'amount' ? sortConfig.direction === 'asc' ? 'up-black' : 'down-black' : 'grey'}.svg`}
                                      className='svg-icon ms-2 custom-sort-icon'
                                    />
                                  </div>
                                </div>
                              </th>
                              <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>PAID AMOUNT</th>
                              <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>REMAINING AMOUNT</th>
                              <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>CURRENCY</th>
                              <th className='align-middle' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>STATUS</th>
                              <th className='align-middle text-center' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>3-WAY MATCH</th>
                              <th className='align-middle text-center procurement-actions-cell' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>ACTIONS</th>
                            </tr>
                          </thead>
                          <tbody className='table-body text-start'>
                            {currentRecords.length === 0 ? (
                              <tr>
                              <td colSpan={15} className='text-center text-muted py-5'>
                                  {isLoading ? 'Loading invoices...' : 'No invoices found.'}
                                </td>
                              </tr>
                            ) : (
                              currentRecords.map((record) => (
                                <tr key={record.id} style={{ borderBottom: '1px solid #E4E6EF' }}>
                                  <td className='text-dark fw-semibold fs-6' style={{ padding: '12px 16px' }}>{record.invoiceNumber}</td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>{displayValue(record.vendorInvoiceNumber)}</td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>{record.invoiceDate}</td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>PO-{record.purchaseOrderId}</td>
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                    {record.requisitionId != null ? `REQ-${record.requisitionId}` : <span className='text-muted'>-</span>}
                                  </td>
                                <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                  {record.requisitionNo || <span className='text-muted'>-</span>}
                                  <div className='text-muted small'>
                                    {record.requisitionTitle || '-'}
                                  </div>
                                </td>
                                <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                  {record.goodsReceiptReference ||
                                    (record.goodsReceiptId ? `GR-${record.goodsReceiptId}` : <span className='text-muted'>-</span>)}
                                </td>
                                <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                  {record.vendorName || (record.vendorId ? `Vendor #${record.vendorId}` : '-')}
                                </td>
                                  {(() => {
                                    const money = resolveInvoiceMoneyPresentation(record, vendorView)
                                    const paidMoney = resolveInvoiceAmountPresentation(
                                      record,
                                      record.paidAmount,
                                      vendorView
                                    )
                                    const remainingMoney = resolveInvoiceAmountPresentation(
                                      record,
                                      record.remainingAmount ??
                                        Number(record.amount || 0) - Number(record.paidAmount || 0),
                                      vendorView
                                    )
                                    return (
                                      <>
                                        <td className='text-dark fw-semibold fs-6' style={{ padding: '12px 16px' }}>
                                          <MoneyDisplay
                                            compact
                                            primaryAmount={money.primaryAmount}
                                            primaryCurrency={money.primaryCurrency}
                                            secondaryAmount={money.secondaryAmount}
                                            secondaryCurrency={money.secondaryCurrency}
                                            sameCurrency={money.sameCurrency}
                                            exchangeRateDisplay={money.auxiliaryText || money.exchangeRateDisplay}
                                          />
                                        </td>
                                        <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                          <MoneyDisplay
                                            compact
                                            primaryAmount={paidMoney.primaryAmount}
                                            primaryCurrency={paidMoney.primaryCurrency}
                                            secondaryAmount={paidMoney.secondaryAmount}
                                            secondaryCurrency={paidMoney.secondaryCurrency}
                                            sameCurrency={paidMoney.sameCurrency}
                                            exchangeRateDisplay={paidMoney.auxiliaryText || paidMoney.exchangeRateDisplay}
                                          />
                                        </td>
                                        <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                          <MoneyDisplay
                                            compact
                                            primaryAmount={remainingMoney.primaryAmount}
                                            primaryCurrency={remainingMoney.primaryCurrency}
                                            secondaryAmount={remainingMoney.secondaryAmount}
                                            secondaryCurrency={remainingMoney.secondaryCurrency}
                                            sameCurrency={remainingMoney.sameCurrency}
                                            exchangeRateDisplay={remainingMoney.auxiliaryText || remainingMoney.exchangeRateDisplay}
                                          />
                                        </td>
                                      </>
                                    )
                                  })()}
                                  <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                    {resolveInvoiceOriginalCurrency(record)}
                                  </td>
                                  <td style={{ padding: '12px 16px' }}>{getStatusBadge(record.status)}</td>
                                  <td className='text-center' style={{ padding: '12px 16px' }}>
                                    {renderThreeWayMatchBadge(record)}
                                  </td>
                                  <td className='text-center procurement-actions-cell' style={{ padding: '12px 16px' }}>
                                    <ProcurementActionGrid>
                                    <button
                                      className='btn btn-sm px-0'
                                      title='View'
                                      onClick={() => handleViewInvoice(record.id)}
                                    >
                                      <KTSVG path='/media/map/ph_eye.svg' />
                                    </button>
                                    <button
                                      className='btn btn-sm btn-light-primary ms-2'
                                      title='View Approval'
                                      onClick={() => handleViewInvoice(record.id)}
                                    >
                                      View Approval
                                    </button>
                                    {canDownloadInvoice && (
                                      <button
                                        className='btn btn-sm btn-primary ms-2'
                                        title='Download PDF'
                                        onClick={() => {
                                          void handleDownloadInvoicePdf(record.id)
                                        }}
                                      >
                                        Download
                                      </button>
                                    )}
                                    {canEditInvoice &&
                                      (record.status === 'APPROVED' || record.status === 'PARTIALLY_PAID') && (
                                        <div className='procurement-action-subgrid d-flex gap-2 justify-content-end'>
                                          {(() => {
                                            const remainingAmount = Math.max(
                                              Number(record.remainingAmount ?? Number(record.amount || 0) - Number(record.paidAmount || 0)),
                                              0,
                                            )
                                            const paymentUnavailable = remainingAmount <= 0
                                            return (
                                              <>
                                                <button
                                                  className='btn btn-sm btn-warning text-dark'
                                                  title='Mark Partially Paid'
                                                  onClick={() => handleMarkPartialPaid(record)}
                                                  disabled={paymentUnavailable}
                                                >
                                                  Mark Partially Paid
                                                </button>
                                                <button
                                                  className='btn btn-sm btn-success'
                                                  title='Mark as Paid'
                                                  onClick={() => handleMarkAsPaid(record.id)}
                                                  disabled={paymentUnavailable}
                                                >
                                                  Mark as Paid
                                                </button>
                                              </>
                                            )
                                          })()}
                                        </div>
                                    )}
                                    </ProcurementActionGrid>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      <div
                        className='pagination-wrapper d-flex justify-content-between align-items-center py-3'
                        style={{
                          position: 'static',
                          bottom: 0,
                          backgroundColor: '#fff',
                          zIndex: 10,
                          borderTop: '1px solid #dee2e6',
                          marginTop: 'auto'
                        }}
                      >
                        <div className='d-flex align-items-center'>
                          <span className='text-muted me-2'>Rows per page</span>
                          <SearchableSelect
                            className='form-select'
                            style={{
                              borderRadius: '20px',
                              width: '70px',
                              border: '1px solid #dee2e6',
                              fontSize: '14px',
                              padding: '4px 8px'
                            }}
                            value={rowsPerPage}
                            onChange={handleRowsPerPageChange}
                          >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                          </SearchableSelect>
                        </div>
                        <div className='d-flex align-items-center'>
                          <span className='text-muted me-3' style={{ fontSize: '14px' }}>
                            Showing <strong>{currentRecords.length > 0 ? ((currentPage - 1) * rowsPerPage) + 1 : 0}-{Math.min(currentPage * rowsPerPage, pendingTotalElements)}</strong> of <strong>{pendingTotalElements}</strong>
                          </span>

                          <nav>
                            <ul className='pagination pagination-sm mb-0' style={{ gap: '2px' }}>
                              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    borderRadius: '6px'
                                  }}
                                  onClick={() => handlePageChange(currentPage - 1)}
                                  disabled={currentPage === 1}
                                >
                                  ‹
                                </button>
                              </li>

                              {(() => {
                                const pages = []
                                const showPages = 5
                                let startPage = Math.max(1, currentPage - 2)
                                let endPage = Math.min(totalPages, startPage + showPages - 1)

                                if (endPage - startPage < showPages - 1) {
                                  startPage = Math.max(1, endPage - showPages + 1)
                                }

                                if (startPage > 1) {
                                  pages.push(
                                    <li key={1} className='page-item'>
                                      <button
                                        className='page-link text-muted'
                                        style={{
                                          backgroundColor: '#f8f9fa',
                                          border: '1px solid #dee2e6',
                                          padding: '8px 12px',
                                          fontSize: '14px',
                                          minWidth: '40px',
                                          borderRadius: '6px'
                                        }}
                                        onClick={() => handlePageChange(1)}
                                      >
                                        1
                                      </button>
                                    </li>
                                  )

                                  if (startPage > 2) {
                                    pages.push(
                                      <li key='ellipsis1' className='page-item disabled'>
                                        <span className='page-link border-0 text-muted' style={{ backgroundColor: 'transparent', padding: '4px 8px' }}>...</span>
                                      </li>
                                    )
                                  }
                                }

                                for (let i = startPage; i <= endPage; i++) {
                                  pages.push(
                                    <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                                      <button
                                        className='page-link text-muted'
                                        style={{
                                          backgroundColor: currentPage === i ? '#F4F9FF' : 'transparent',
                                          border: '1px solid #dee2e6',
                                          padding: '8px 12px',
                                          fontSize: '14px',
                                          minWidth: '40px',
                                          borderRadius: '6px',
                                          outline: 'none',
                                          boxShadow: 'none'
                                        }}
                                        onClick={() => handlePageChange(i)}
                                      >
                                        {i}
                                      </button>
                                    </li>
                                  )
                                }

                                if (endPage < totalPages) {
                                  if (endPage < totalPages - 1) {
                                    pages.push(
                                      <li key='ellipsis2' className='page-item disabled'>
                                        <span className='page-link border-0 text-muted' style={{ backgroundColor: 'transparent', padding: '4px 8px' }}>...</span>
                                      </li>
                                    )
                                  }

                                  pages.push(
                                    <li key={totalPages} className='page-item'>
                                      <button
                                        className='page-link text-muted'
                                        style={{
                                          backgroundColor: '#f8f9fa',
                                          border: '1px solid #dee2e6',
                                          padding: '8px 12px',
                                          fontSize: '14px',
                                          minWidth: '40px',
                                          borderRadius: '6px'
                                        }}
                                        onClick={() => handlePageChange(totalPages)}
                                      >
                                        {totalPages}
                                      </button>
                                    </li>
                                  )
                                }

                                return pages
                              })()}

                              <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                                <button
                                  className='page-link text-muted'
                                  style={{
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #dee2e6',
                                    padding: '8px 12px',
                                    fontSize: '14px',
                                    borderRadius: '6px'
                                  }}
                                  onClick={() => handlePageChange(currentPage + 1)}
                                  disabled={currentPage === totalPages || totalPages === 0}
                                >
                                  ›
                                </button>
                              </li>
                            </ul>
                          </nav>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Past (Paid) Invoices */}
              {activeTab === 'paid' && (
                <div className='mt-4'>
                  <h4 className='fw-bold text-primary mb-4'>Past Invoices (Paid) ({sortedPaid.length} entries)</h4>
                  {paidPoGroups.length === 0 ? (
                    <div className='text-center text-muted py-5 border rounded-3 bg-light fw-semibold'>No paid invoices yet.</div>
                  ) : (
                    <div className='d-flex flex-column gap-4'>
                      {paidPoGroups.map((group) => {
                        const expanded = expandedPaidPoIds[group.poId] ?? false
                        return (
                          <div key={`paid-po-${group.poId}`} className='border rounded-3 bg-white shadow-sm overflow-hidden'>
                            <button
                              type='button'
                              className='w-100 text-start border-0 bg-white px-4 py-3'
                              aria-expanded={expanded}
                              onClick={() =>
                                setExpandedPaidPoIds((prev) => ({ ...prev, [group.poId]: !expanded }))
                              }
                            >
                              <div className='d-flex justify-content-between align-items-start gap-4 flex-wrap'>
                                <div className='pe-3'>
                                  <div className='fw-bold text-dark mb-1 fs-5 d-flex align-items-center gap-2'>
                                    <span style={{ fontSize: '14px' }}>{expanded ? '▾' : '▸'}</span>
                                    <span>{group.poLabel}</span>
                                  </div>
                                  <div className='text-muted fs-7'>
                                    {group.invoices.length} invoice{group.invoices.length > 1 ? 's' : ''} grouped
                                    <span className='ms-3 badge bg-light-success text-success'>
                                      3-way matched {group.matchedCount}/{group.invoices.length}
                                    </span>
                                  </div>
                                </div>
                                <div className='d-flex flex-wrap gap-2 align-items-stretch'>
                                  <div className='border rounded-2 bg-light px-3 py-2'>
                                    <div className='text-muted fs-8 text-uppercase'>Total Amount</div>
                                    <div className='fw-bold text-dark fs-6'>{formatMoney(group.currency, group.totalAmount)}</div>
                                  </div>
                                  <div className='border rounded-2 bg-light px-3 py-2'>
                                    <div className='text-muted fs-8 text-uppercase'>Paid Amount</div>
                                    <div className='fw-bold text-success fs-6'>{formatMoney(group.currency, group.paidAmount)}</div>
                                  </div>
                                  <div className='border rounded-2 bg-light px-3 py-2'>
                                    <div className='text-muted fs-8 text-uppercase'>Remaining Amount</div>
                                    <div className='fw-bold text-warning fs-6'>{formatMoney(group.currency, group.remainingAmount)}</div>
                                  </div>
                                  <div className='border rounded-2 bg-light px-3 py-2'>
                                    <div className='text-muted fs-8 text-uppercase'>Latest Invoice ID</div>
                                    <div className='fw-bold text-dark fs-6'>#{group.latestInvoiceId}</div>
                                  </div>
                                  <div className='border rounded-2 bg-light-primary px-3 py-2 text-end'>
                                    <div className='text-muted fs-8 text-uppercase'>Expand to View</div>
                                    <div className='fw-bold text-primary fs-6'>{expanded ? 'Hide Invoices' : 'Show Invoices'}</div>
                                  </div>
                                </div>
                              </div>
                            </button>
                            {expanded && (
                              <div className='border-top p-3 bg-light bg-opacity-25'>
                                <div className='report-table table-responsive'>
                                  <table className='table table-bordered table-striped align-middle mb-0 bg-white'>
                                    <thead className='table-header text-start'>
                                      <tr>
                                        <th style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>INVOICE NUMBER</th>
                                        <th style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>INVOICE DATE</th>
                                        <th style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>GR ID</th>
                                        <th style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>TOTAL AMOUNT</th>
                                        <th style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>PAID AMOUNT</th>
                                        <th style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>REMAINING AMOUNT</th>
                                        <th style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>CURRENCY</th>
                                        <th style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>STATUS</th>
                                        <th className='text-center' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>3-WAY MATCH</th>
                                        <th className='text-center procurement-actions-cell' style={{ padding: '12px 16px', color: '#3F4254', fontWeight: 600 }}>ACTIONS</th>
                                      </tr>
                                    </thead>
                                    <tbody className='table-body text-start'>
                                      {group.invoices.map((record) => {
                                        const money = resolveInvoiceMoneyPresentation(record, vendorView)
                                        const paidMoney = resolveInvoiceAmountPresentation(
                                          record,
                                          record.paidAmount,
                                          vendorView
                                        )
                                        const remainingMoney = resolveInvoiceAmountPresentation(
                                          record,
                                          record.remainingAmount ??
                                            Number(record.amount || 0) - Number(record.paidAmount || 0),
                                          vendorView
                                        )
                                        return (
                                          <tr key={`paid-invoice-${record.id}`} style={{ borderBottom: '1px solid #E4E6EF' }}>
                                            <td className='text-dark fw-semibold fs-6' style={{ padding: '12px 16px' }}>{record.invoiceNumber}</td>
                                            <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>{record.invoiceDate}</td>
                                            <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                              {record.goodsReceiptId ? `GR-${record.goodsReceiptId}` : <span className='text-muted'>-</span>}
                                            </td>
                                            <td className='text-dark fw-semibold fs-6' style={{ padding: '12px 16px' }}>
                                              <MoneyDisplay
                                                compact
                                                primaryAmount={money.primaryAmount}
                                                primaryCurrency={money.primaryCurrency}
                                                secondaryAmount={money.secondaryAmount}
                                                secondaryCurrency={money.secondaryCurrency}
                                                sameCurrency={money.sameCurrency}
                                                exchangeRateDisplay={money.auxiliaryText || money.exchangeRateDisplay}
                                              />
                                            </td>
                                            <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                              <MoneyDisplay
                                                compact
                                                primaryAmount={paidMoney.primaryAmount}
                                                primaryCurrency={paidMoney.primaryCurrency}
                                                secondaryAmount={paidMoney.secondaryAmount}
                                                secondaryCurrency={paidMoney.secondaryCurrency}
                                                sameCurrency={paidMoney.sameCurrency}
                                                exchangeRateDisplay={paidMoney.auxiliaryText || paidMoney.exchangeRateDisplay}
                                              />
                                            </td>
                                            <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                              <MoneyDisplay
                                                compact
                                                primaryAmount={remainingMoney.primaryAmount}
                                                primaryCurrency={remainingMoney.primaryCurrency}
                                                secondaryAmount={remainingMoney.secondaryAmount}
                                                secondaryCurrency={remainingMoney.secondaryCurrency}
                                                sameCurrency={remainingMoney.sameCurrency}
                                                exchangeRateDisplay={remainingMoney.auxiliaryText || remainingMoney.exchangeRateDisplay}
                                              />
                                            </td>
                                            <td className='text-dark fs-6' style={{ padding: '12px 16px' }}>
                                              {resolveInvoiceOriginalCurrency(record)}
                                            </td>
                                            <td style={{ padding: '12px 16px' }}>{getStatusBadge(record.status)}</td>
                                            <td className='text-center' style={{ padding: '12px 16px' }}>
                                              {renderThreeWayMatchBadge(record)}
                                            </td>
                                            <td className='text-center procurement-actions-cell' style={{ padding: '12px 16px' }}>
                                              <ProcurementActionGrid>
                                              <button
                                                className='btn btn-sm px-0'
                                                title='View'
                                                onClick={() => handleViewInvoice(record.id)}
                                              >
                                                <KTSVG path='/media/map/ph_eye.svg' />
                                              </button>
                                              {canDownloadInvoice && (
                                                <button
                                                  className='btn btn-sm btn-primary ms-2'
                                                  title='Download PDF'
                                                  onClick={() => {
                                                    void handleDownloadInvoicePdf(record.id)
                                                  }}
                                                >
                                                  Download
                                                </button>
                                              )}
                                              </ProcurementActionGrid>
                                            </td>
                                          </tr>
                                        )
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Invoice Modal */}
      {canCreateInvoice && (
        <CreateInvoiceModal
          visible={isCreateModalVisible}
          onClose={handleModalClose}
          onSubmit={handleInvoiceCreated}
        />
      )}

      {/* View Invoice Modal */}
      {isViewModalVisible && (
        <div
          className="modal fade show d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1050,
          }}
        >
          <div className='modal-dialog modal-xl modal-dialog-centered procurement-view-modal-dialog'>
            <div
              className='modal-content'
              style={{ maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
            >
              <div className='modal-header flex-shrink-0'>
                <div>
                  <h5 className='modal-title text-dark mb-1'>Invoice Details</h5>
                  <p className='text-muted mb-0 fs-7'>
                    {displayValue(viewInvoice?.invoiceNumber)}
                  </p>
                </div>
                <button type='button' className='btn-close' onClick={handleCloseViewModal}></button>
              </div>
              <div
                className='modal-body'
                style={{ overflowY: 'auto', flex: '1 1 auto', minHeight: 0, WebkitOverflowScrolling: 'touch' }}
              >
                {isViewLoading && <div className='text-muted'>Loading...</div>}
                {!isViewLoading && viewInvoice && (
                  (() => {
                    const approvalSection = (
                      <div className='mb-6'>
                        <ApprovalDocumentStatusCard
                          moduleType='INVOICE'
                          documentId={Number(viewInvoice.id)}
                          title='Invoice Approval'
                          onChanged={async () => {
                            await loadInvoices()
                            const refreshed = await getInvoiceById(Number(viewInvoice.id))
                            setViewInvoice(refreshed)
                          }}
                        />
                      </div>
                    )
                    const money = resolveInvoiceMoneyPresentation(viewInvoice, vendorView)
                    const invoiceCurrency = displayValue(resolveInvoiceOriginalCurrency(viewInvoice))
                    const invoiceTotal = numberValue(viewInvoice.originalTotal ?? viewInvoice.amount)
                    const paidAmount = numberValue(viewInvoice.paidAmount)
                    const remainingAmount = Math.max(
                      numberValue(viewInvoice.remainingAmount ?? invoiceTotal - paidAmount),
                      0
                    )
                    const documentLines = Array.isArray(viewInvoice.lineItems)
                      ? viewInvoice.lineItems
                      : []
                    const isTechnical = Boolean(viewInvoice.technical)
                    const contextPrimaryLabel = displayValue(viewInvoice.contextPrimaryLabel)
                    const contextSecondaryLabel = displayValue(viewInvoice.contextSecondaryLabel)
                    const contextPrimaryValue = displayValue(viewInvoice.contextPrimaryValue)
                    const contextSecondaryValue = displayValue(viewInvoice.contextSecondaryValue)
                    const totalItemPrice = numberValue(viewInvoice.totalItemPrice)
                    const freightCost = numberValue(viewInvoice.freightCost)
                    const packagingCost = numberValue(viewInvoice.packagingCost)
                    const truckCost = numberValue(viewInvoice.truckCost)
                    const bargeCost = numberValue(viewInvoice.bargeCost)
                    const otherCost = numberValue(viewInvoice.otherCost)
                    const vatOnLogistics = numberValue(viewInvoice.vatOnLogistics)
                    const headerDiscount = numberValue(viewInvoice.headerDiscount)
                    const lineDiscountTotal = numberValue(viewInvoice.lineDiscountTotal)
                    const lineGstVatTotal = numberValue(viewInvoice.lineGstVatTotal)
                    const grandTotal = numberValue(viewInvoice.grandTotal ?? invoiceTotal)

                    return (
                      <div className='d-flex flex-column gap-4'>
                        {approvalSection}
                        <div className='border rounded p-3 bg-light-primary'>
                          <div className='row g-3'>
                            <div className='col-md-4'><strong>Invoice Number:</strong> {displayValue(viewInvoice.invoiceNumber)}</div>
                            <div className='col-md-4'><strong>Vendor&apos;s Invoice Number:</strong> {displayValue(viewInvoice.vendorInvoiceNumber)}</div>
                            <div className='col-md-4'><strong>Invoice Date:</strong> {displayValue(viewInvoice.invoiceDate)}</div>
                            <div className='col-md-4'><strong>Status:</strong> {displayValue(viewInvoice.status)}</div>
                            <div className='col-md-4'><strong>Original Currency:</strong> {invoiceCurrency}</div>
                            <div className='col-md-4'><strong>Company Currency:</strong> {displayValue(viewInvoice.companyCurrencyCode || '-')}</div>
                            <div className='col-md-4'><strong>Locked Rate:</strong> {displayValue(viewInvoice.exchangeRateDisplay || (viewInvoice.exchangeRate != null ? String(viewInvoice.exchangeRate) : '-'))}</div>
                            <div className='col-md-4'>
                              <strong>Invoice Amount:</strong>
                              <div className='mt-1'>
                                <MoneyDisplay
                                  compact
                                  primaryAmount={money.primaryAmount}
                                  primaryCurrency={money.primaryCurrency}
                                  secondaryAmount={money.secondaryAmount}
                                  secondaryCurrency={money.secondaryCurrency}
                                  sameCurrency={money.sameCurrency}
                                  exchangeRateDisplay={money.auxiliaryText || money.exchangeRateDisplay}
                                />
                              </div>
                            </div>
                            <div className='col-md-4'>
                              <strong>Paid Amount:</strong>
                              <div className='mt-1'>
                                {(() => {
                                  const paidMoney = resolveInvoiceAmountPresentation(
                                    viewInvoice,
                                    paidAmount,
                                    vendorView
                                  )
                                  return (
                                    <MoneyDisplay
                                      compact
                                      primaryAmount={paidMoney.primaryAmount}
                                      primaryCurrency={paidMoney.primaryCurrency}
                                      secondaryAmount={paidMoney.secondaryAmount}
                                      secondaryCurrency={paidMoney.secondaryCurrency}
                                      sameCurrency={paidMoney.sameCurrency}
                                      exchangeRateDisplay={paidMoney.auxiliaryText || paidMoney.exchangeRateDisplay}
                                    />
                                  )
                                })()}
                              </div>
                            </div>
                            <div className='col-md-4'>
                              <strong>Remaining Amount:</strong>
                              <div className='mt-1'>
                                {(() => {
                                  const remainingMoney = resolveInvoiceAmountPresentation(
                                    viewInvoice,
                                    remainingAmount,
                                    vendorView
                                  )
                                  return (
                                    <MoneyDisplay
                                      compact
                                      primaryAmount={remainingMoney.primaryAmount}
                                      primaryCurrency={remainingMoney.primaryCurrency}
                                      secondaryAmount={remainingMoney.secondaryAmount}
                                      secondaryCurrency={remainingMoney.secondaryCurrency}
                                      sameCurrency={remainingMoney.sameCurrency}
                                      exchangeRateDisplay={
                                        remainingMoney.auxiliaryText || remainingMoney.exchangeRateDisplay
                                      }
                                    />
                                  )
                                })()}
                              </div>
                            </div>
                            <div className='col-md-4'>
                              <strong>PO Aggregate 3-Way Match:</strong>{' '}
                              {renderThreeWayMatchBadge(viewInvoice)}
                            </div>
                            {buildInvoiceThreeWayMatchAmounts(
                              viewInvoice,
                              invoiceCurrency
                            ).map((row) => (
                              <div key={row.label} className='col-md-4'>
                                <strong>{row.label}:</strong> {row.value}
                              </div>
                            ))}
                            {viewInvoice.threeWayMatchSummary && (
                              <div className='col-12 text-muted fs-7'>
                                {viewInvoice.threeWayMatchSummary}
                              </div>
                            )}
                            <div className='col-md-4'><strong>Vendor:</strong> {displayValue(viewInvoice.vendorName || `V-${viewInvoice.vendorId}`)}</div>
                            <div className='col-md-4'><strong>PO Ref:</strong> {displayValue(viewInvoice.poNumber || `PO-${viewInvoice.purchaseOrderId}`)}</div>
                            <div className='col-md-4'><strong>Vendor Ref:</strong> {displayValue(viewInvoice.vendorReferenceNo || viewInvoice.supplierReference || '-')}</div>
                            <div className='col-md-4'><strong>Requisition No:</strong> {displayValue(viewInvoice.requisitionNo || '-')}</div>
                            <div className='col-md-4'><strong>GR Ref:</strong> {displayValue(viewInvoice.goodsReceiptReference || (viewInvoice.goodsReceiptId ? `GR-${viewInvoice.goodsReceiptId}` : '-'))}</div>
                          </div>
                        </div>

                        <div className='border rounded p-3'>
                          <h6 className='fw-bold mb-3 text-primary'>PO Details</h6>
                          <div className='row g-3'>
                            <div className='col-md-4'><strong>PO Number:</strong> {displayValue(viewInvoice.poNumber || `PO-${viewInvoice.purchaseOrderId}`)}</div>
                            <div className='col-md-4'><strong>PO Date:</strong> {displayValue(viewInvoice.poDate)}</div>
                            <div className='col-md-4'><strong>Company Header:</strong> {displayValue(viewInvoice.companyHeader)}</div>
                            <div className='col-md-6'><strong>Issued To / Invoice Address:</strong> {displayValue(viewInvoice.vendorName)} / {displayValue(viewInvoice.invoiceAddressedTo)}</div>
                            <div className='col-md-6'><strong>Issued By / Invoice Address:</strong> {displayValue(viewInvoice.poRaisedByUser)} / {displayValue(viewInvoice.invoiceAddressedTo)}</div>
                            <div className='col-md-4'><strong>Supplier Reference:</strong> {displayValue(viewInvoice.supplierReference)}</div>
                            <div className='col-md-4'><strong>PO Currency:</strong> {displayValue(viewInvoice.purchaseOrderCurrency || invoiceCurrency)}</div>
                            <div className='col-md-4'><strong>Delivery Date:</strong> {displayValue(viewInvoice.deliveryDate)}</div>
                            <div className='col-md-4'><strong>Status:</strong> {displayValue(viewInvoice.purchaseOrderStatus)}</div>
                            <div className='col-md-4'><strong>Approver:</strong> {displayValue(viewInvoice.poApprover)}</div>
                            <div className='col-md-4'><strong>Logistic Account Code:</strong> {displayValue(viewInvoice.logisticAccountCode)}</div>
                          </div>
                        </div>

                        <div className='border rounded p-3'>
                          <h6 className='fw-bold mb-3 text-primary'>Requisition / RFQ / Quotation Context</h6>
                          <div className='row g-3'>
                            <div className='col-md-4'><strong>Requisition Number:</strong> {displayValue(viewInvoice.requisitionNo)}</div>
                            <div className='col-md-4'><strong>Requisition Date:</strong> {displayValue(viewInvoice.requisitionDate)}</div>
                            <div className='col-md-4'><strong>Requisition Title:</strong> {displayValue(viewInvoice.requisitionTitle)}</div>
                            <div className='col-md-4'><strong>Linked Defect:</strong> {displayValue(viewInvoice.linkedDefectDisplay)}</div>
                            <div className='col-md-4'>
                              <strong>Vessel:</strong>{' '}
                              <VesselLandingLink
                                vesselId={Number.isFinite(Number(viewInvoice.vesselId)) && Number(viewInvoice.vesselId) > 0 ? Number(viewInvoice.vesselId) : null}
                                label={displayValue(viewInvoice.vesselName)}
                                className='text-dark text-decoration-underline'
                              />
                            </div>
                            <div className='col-md-4'><strong>{contextPrimaryLabel}:</strong> {contextPrimaryValue}</div>
                            <div className='col-md-4'><strong>{contextSecondaryLabel}:</strong> {contextSecondaryValue}</div>
                            <div className='col-md-4'><strong>Urgency:</strong> {displayValue(viewInvoice.urgency)}</div>
                            <div className='col-md-4'><strong>Delivery Port:</strong> {displayValue(viewInvoice.deliveryPort)}</div>
                            <div className='col-md-4'><strong>RFQ Ref:</strong> {displayValue(viewInvoice.rfqReference)}</div>
                            <div className='col-md-4'><strong>Quotation Context:</strong> {displayValue(viewInvoice.quotationContext)}</div>
                          </div>
                        </div>

                        <div className='border rounded p-3'>
                          <h6 className='fw-bold mb-3 text-primary'>Item Details</h6>
                          <div className='table-responsive'>
                            <table className='table table-sm table-bordered align-middle mb-0'>
                              <thead className='table-light'>
                                <tr>
                                  <th>#</th>
                                  <th>Item Description</th>
                                  {isTechnical ? (
                                    <>
                                      <th>{contextPrimaryLabel}</th>
                                      <th>{contextSecondaryLabel}</th>
                                      <th>Part</th>
                                      <th>Part No</th>
                                      <th>Drawing No</th>
                                    </>
                                  ) : (
                                    <>
                                      <th>{contextPrimaryLabel}</th>
                                      <th>{contextSecondaryLabel}</th>
                                    </>
                                  )}
                                  <th>Qty</th>
                                  <th>Req Unit</th>
                                  <th>PO Unit</th>
                                  <th>Invoice Qty</th>
                                  <th>Unit Price</th>
                                  <th>Discount %</th>
                                  <th>GST/VAT %</th>
                                  <th>Total</th>
                                  <th>Account Code</th>
                                  <th>Sub Account Code</th>
                                </tr>
                              </thead>
                              <tbody>
                                {documentLines.length === 0 ? (
                                  <tr>
                                    <td colSpan={isTechnical ? 17 : 14} className='text-center text-muted py-3'>
                                      No line details available.
                                    </td>
                                  </tr>
                                ) : (
                                  documentLines.map((line, idx) => (
                                    <tr key={`invoice-line-${idx}`}>
                                      <td>{idx + 1}</td>
                                      <td>{displayValue(line.itemDescription)}</td>
                                      {isTechnical ? (
                                        <>
                                          <td>{displayValue(line.equipmentName)}</td>
                                          <td>{displayValue(line.subcomponentName)}</td>
                                          <td>{displayValue(line.partName)}</td>
                                          <td>{displayValue(line.partNo)}</td>
                                          <td>{displayValue(line.drawingNo)}</td>
                                        </>
                                      ) : (
                                        <>
                                          <td>{contextPrimaryValue}</td>
                                          <td>{contextSecondaryValue}</td>
                                        </>
                                      )}
                                      <td>{numberValue(line.qty)}</td>
                                      <td>{displayValue(line.reqUnit)}</td>
                                      <td>{displayValue(line.poUnit)}</td>
                                      <td>{displayValue(line.invoiceQty)}</td>
                                      <td>{formatMoney(invoiceCurrency, line.unitPrice)}</td>
                                      <td>{numberValue(line.discountPct).toFixed(2)}</td>
                                      <td>{numberValue(line.gstPct).toFixed(2)}</td>
                                      <td>{formatMoney(invoiceCurrency, line.total)}</td>
                                      <td>{displayValue(line.accountCode)}</td>
                                      <td>{displayValue(line.subAccountCode)}</td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Invoice Attachments */}
                        <div className='border rounded p-3'>
                          <h6 className='fw-bold mb-3 text-primary'>Attachments</h6>
                          <ProcurementAttachmentGallery
                            attachments={(viewInvoice.attachments || []).map((a) => ({
                              id: a.registryId ?? a.id,
                              fileName: a.fileName || 'attachment',
                              sizeBytes: a.size ?? (a as any).sizeBytes ?? null,
                              attachmentKey:
                                a.registryId != null
                                  ? String(a.registryId)
                                  : a.id != null
                                    ? String(a.id)
                                    : null,
                              url: a.downloadUrl || a.viewUrl || undefined,
                              available:
                                a.canView !== false && a.canDownload !== false,
                            }))}
                            emptyText='No attachments uploaded for this invoice.'
                          />
                        </div>

                        <div className='border rounded p-3'>
                          <h6 className='fw-bold mb-3 text-primary'>Commercial Summary</h6>
                          <div className='row g-3'>
                            <div className='col-md-4'><strong>Total Item Price:</strong> {formatMoney(invoiceCurrency, totalItemPrice)}</div>
                            <div className='col-md-4'><strong>Freight/Trucking:</strong> {formatMoney(invoiceCurrency, freightCost)}</div>
                            <div className='col-md-4'><strong>Pkg & Handling:</strong> {formatMoney(invoiceCurrency, packagingCost)}</div>
                            <div className='col-md-4'><strong>Truck Cost:</strong> {formatMoney(invoiceCurrency, truckCost)}</div>
                            <div className='col-md-4'><strong>Barge/Workboat:</strong> {formatMoney(invoiceCurrency, bargeCost)}</div>
                            <div className='col-md-4'><strong>Other Cost:</strong> {formatMoney(invoiceCurrency, otherCost)}</div>
                            <div className='col-md-4'><strong>GST/VAT on Logistic/Other:</strong> {formatMoney(invoiceCurrency, vatOnLogistics)}</div>
                            <div className='col-md-4'><strong>Line Discount Total:</strong> {formatMoney(invoiceCurrency, lineDiscountTotal)}</div>
                            <div className='col-md-4'><strong>Header Discount:</strong> {formatMoney(invoiceCurrency, headerDiscount)}</div>
                            <div className='col-md-4'><strong>Line GST/VAT Total:</strong> {formatMoney(invoiceCurrency, lineGstVatTotal)}</div>
                            <div className='col-md-4'><strong>Grand Total:</strong> {formatMoney(invoiceCurrency, grandTotal)}</div>
                          </div>
                        </div>
                      </div>
                    )
                  })()
                )}
              </div>
              <div className='modal-footer flex-shrink-0 border-top'>
                <button
                  className='btn btn-primary btn-sm'
                  onClick={() => {
                    if (!viewInvoice) return
                    void handleDownloadInvoicePdf(viewInvoice)
                  }}
                >
                  Download PDF
                </button>
                <button className='btn btn-light btn-sm' onClick={handleCloseViewModal}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {invoiceDownloadDoc && (
        <div
          style={{
            position: 'fixed',
            left: '-20000px',
            top: 0,
            width: '1100px',
            opacity: 1,
            pointerEvents: 'none',
            background: '#fff',
          }}
        >
          <div id='invoice-download-host'>
            <InvoiceProcurementDocument
              invoice={invoiceDownloadDoc.invoice}
              purchaseOrder={invoiceDownloadDoc.purchaseOrder}
              goodsReceipt={invoiceDownloadDoc.goodsReceipt}
              requisition={invoiceDownloadDoc.requisition}
            />
          </div>
        </div>
      )}

      {/* Partial Paid Modal */}
      {isPartialModalVisible && (
        <div
          className="modal fade show d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: 1050,
          }}
        >
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title text-dark'>Mark Invoice as Partially Paid</h5>
                <button type='button' className='btn-close' onClick={() => setIsPartialModalVisible(false)}></button>
              </div>
              <div className='modal-body'>
                {(() => {
                  const money = partialInvoice ? resolveInvoiceMoneyPresentation(partialInvoice, vendorView) : null
                  return (
                <div className='mb-2 text-dark'>
                  <div><strong>Invoice #:</strong> {partialInvoice?.invoiceNumber || '-'}</div>
                  <div>
                    <strong>Total Amount:</strong>{' '}
                    {money ? (
                      <MoneyDisplay
                        compact
                        primaryAmount={money.primaryAmount}
                        primaryCurrency={money.primaryCurrency}
                        secondaryAmount={money.secondaryAmount}
                        secondaryCurrency={money.secondaryCurrency}
                        sameCurrency={money.sameCurrency}
                        exchangeRateDisplay={money.auxiliaryText || money.exchangeRateDisplay}
                      />
                    ) : '-'}
                  </div>
                  <div><strong>Paid:</strong> {partialInvoice ? formatMoney(resolveInvoiceOriginalCurrency(partialInvoice), Number(partialInvoice.paidAmount || 0)) : '-'}</div>
                  <div><strong>Remaining:</strong> {partialInvoice ? formatMoney(resolveInvoiceOriginalCurrency(partialInvoice), Math.max(Number(partialInvoice.remainingAmount ?? Number(partialInvoice.amount || 0) - Number(partialInvoice.paidAmount || 0)), 0)) : '-'}</div>
                </div>
                  )
                })()}
                <label className='form-label text-dark'>Amount Paid</label>
                <input
                  type='number'
                  className='form-control'
                  value={partialAmount}
                  onChange={(e) => setPartialAmount(e.target.value)}
                />
                <small className='text-muted'>Enter the amount received for this invoice.</small>
              </div>
              <div className='modal-footer'>
                <button className='btn btn-light btn-sm' onClick={() => setIsPartialModalVisible(false)}>Cancel</button>
                <button className='btn btn-warning btn-sm text-dark' onClick={submitPartialPaid}>Confirm</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Container */}
      <ToastContainer
        position="top-center"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        style={{ top: "5rem" }}
      />
    </div>
  )
}

export default InvoiceManagement
